import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
        setMobileCatsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAll = () => {
    setMobileMenuOpen(false);
    setMobileCatsOpen(false);
    setDropdownOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            onClick={closeAll}
          >
            <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-base text-background-50"></i>
            </div>
            <span className="hidden md:block text-lg font-heading font-semibold text-foreground-950 tracking-tight">
              Itaobuy
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Home
            </Link>
            <Link
              to="/guida-itaobuy-spreadsheet"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Guida
            </Link>
            <Link
              to="/guida-itaobuy-spreadsheet"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Shopping Guide
            </Link>
            <Link
              to="/guida-itaobuy-spreadsheet"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Prodotti
            </Link>

            {/* Desktop Categorie Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Categorie</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden max-h-[450px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors border-b border-background-100 last:border-b-0 whitespace-nowrap cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <img
                        src={cat.thumb}
                        alt={cat.name}
                        className="w-10 h-10 rounded-lg object-cover object-top flex-shrink-0"
                      />
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* CTA Buttons (Desktop) */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary-100 text-sm font-semibold text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Itaobuy Sheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-xs font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
            <button
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
                setMobileCatsOpen(false);
              }}
              className="w-10 h-10 rounded-full flex items-center justify-center bg-background-100 border border-background-200/70 text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div
          ref={mobileRef}
          className="md:hidden fixed top-[72px] left-0 right-0 bottom-0 bg-background-50 border-t border-background-200/60 overflow-y-auto z-40"
        >
          <div className="p-6">
            {/* Mobile Nav Links */}
            <div className="flex flex-col gap-1 mb-6">
              <Link
                to="/"
                onClick={closeAll}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors text-foreground-800 font-medium"
              >
                <i className="ri-home-4-line text-lg w-6 h-6 flex items-center justify-center"></i>
                Home
              </Link>
              <Link
                to="/guida-itaobuy-spreadsheet"
                onClick={closeAll}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors text-foreground-800 font-medium"
              >
                <i className="ri-book-open-line text-lg w-6 h-6 flex items-center justify-center"></i>
                Guida Completa
              </Link>
              <Link
                to="/guida-itaobuy-spreadsheet"
                onClick={closeAll}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors text-foreground-800 font-medium"
              >
                <i className="ri-shopping-cart-2-line text-lg w-6 h-6 flex items-center justify-center"></i>
                Shopping Guide
              </Link>
            </div>

            {/* Mobile Categories Toggle */}
            <button
              onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-foreground-800 font-medium mb-3 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <i className="ri-grid-line text-lg w-6 h-6 flex items-center justify-center"></i>
                <span>Categorie</span>
              </div>
              <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${mobileCatsOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatsOpen && (
              <div className="flex flex-col gap-1 mb-6 ml-2">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-background-100 transition-colors text-foreground-700 text-sm cursor-pointer"
                    onClick={closeAll}
                  >
                    <img
                      src={cat.thumb}
                      alt={cat.name}
                      className="w-9 h-9 rounded-lg object-cover object-top flex-shrink-0"
                    />
                    <span>{cat.name}</span>
                  </a>
                ))}
              </div>
            )}

            {/* Mobile Article Links */}
            <div className="border-t border-background-200/60 pt-4">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3 px-4">Guide SEO</p>
              <div className="flex flex-col gap-1">
                {[
                  { label: 'Sicurezza e Affidabilità', slug: '/sicurezza-itaobuy-spreadsheet', icon: 'ri-shield-check-line' },
                  { label: 'Migliori Prodotti', slug: '/prodotti-migliori-itaobuy', icon: 'ri-star-line' },
                  { label: 'Come Funziona', slug: '/come-funziona-itaobuy-spreadsheet', icon: 'ri-question-line' },
                  { label: 'Recensioni Utenti', slug: '/recensioni-itaobuy-spreadsheet', icon: 'ri-chat-1-line' },
                  { label: 'Tutorial Avanzato', slug: '/tutorial-itaobuy-spreadsheet', icon: 'ri-code-line' },
                  { label: 'Confronto Alternative', slug: '/confronto-itaobuy-spreadsheet', icon: 'ri-scales-line' },
                ].map((item) => (
                  <Link
                    key={item.slug}
                    to={item.slug}
                    onClick={closeAll}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-background-100 transition-colors text-foreground-700 text-sm"
                  >
                    <i className={`${item.icon} text-base w-6 h-6 flex items-center justify-center`}></i>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Mobile CTA */}
            <div className="mt-6 flex flex-col gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full text-center px-5 py-3 rounded-full bg-secondary-100 text-sm font-semibold text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                onClick={closeAll}
              >
                Itaobuy Sheet
                <i className="ri-arrow-right-up-line text-sm ml-2"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full text-center px-5 py-3 rounded-full bg-primary-500 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                onClick={closeAll}
              >
                Buy It Now
                <i className="ri-arrow-right-up-line text-sm ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}